import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

interface TrendDataPoint {
  date: string;
  match_rate: number;
  exact_rate: number;
  fuzzy_rate: number;
  orphan_rate: number;
  total_reads: number;
  total_sessions: number;
  exact_matches?: number;
  fuzzy_matches?: number;
}

interface PerformanceTrendChartProps {
  data: TrendDataPoint[];
  byLocationData?: Record<string, TrendDataPoint[]>;
  loading?: boolean;
  targetMatchRate?: number;
}

const METRIC_COLORS = {
  match_rate: "#E86E30", // Verge orange - primary metric
  exact_rate: "#10b981", // Green - good
  fuzzy_rate: "#f59e0b", // Amber - attention
  orphan_rate: "#ef4444", // Red - problem
};

const METRIC_LABELS = {
  match_rate: "Match Rate",
  exact_rate: "Exact Match",
  fuzzy_rate: "Fuzzy Match",
  orphan_rate: "Orphan Rate",
};

const LOCATION_COLORS = [
  "#E86E30", // Verge orange
  "#10b981", // Green
  "#f59e0b", // Amber
  "#ef4444", // Red
  "#8b5cf6", // Purple
  "#ec4899", // Pink
  "#14b8a6", // Teal
  "#f97316", // Orange
];

export function PerformanceTrendChart({
  data,
  byLocationData = {},
  loading = false,
  targetMatchRate = 85,
}: PerformanceTrendChartProps) {
  // View mode: 'overall' or 'by-zone'
  const [viewMode, setViewMode] = useState<"overall" | "by-zone">("overall");

  // Toggle visibility of metrics (only for overall view)
  const [visibleMetrics, setVisibleMetrics] = useState<Record<string, boolean>>(
    {
      match_rate: true,
      exact_rate: true,
      fuzzy_rate: true,
      orphan_rate: true,
    },
  );

  const toggleMetric = (metric: string) => {
    setVisibleMetrics((prev) => ({
      ...prev,
      [metric]: !prev[metric],
    }));
  };

  // Calculate trend (last 7 days vs previous 7 days)
  const calculateTrend = () => {
    if (data.length < 7) return null;

    const recent = data.slice(-7);
    const previous = data.slice(-14, -7);

    const recentAvg =
      recent.reduce((sum, d) => sum + d.match_rate, 0) / recent.length;
    const previousAvg =
      previous.reduce((sum, d) => sum + d.match_rate, 0) / previous.length;

    const change = recentAvg - previousAvg;
    return {
      value: Math.abs(change).toFixed(1),
      isPositive: change >= 0,
      recentAvg: recentAvg.toFixed(1),
    };
  };

  const trend = calculateTrend();

  // Custom tooltip with full context
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
        <div className="font-semibold mb-2">
          {new Date(data.date).toLocaleDateString()}
        </div>
        <div className="space-y-1 text-sm">
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Match Rate:</span>
            <span className="font-medium text-primary">
              {data.match_rate}%
            </span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Exact:</span>
            <span className="font-medium text-green-600 dark:text-green-400">
              {data.exact_rate}%
            </span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Fuzzy:</span>
            <span className="font-medium text-amber-600 dark:text-amber-400">
              {data.fuzzy_rate}%
            </span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Orphan:</span>
            <span className="font-medium text-red-600 dark:text-red-400">
              {data.orphan_rate}%
            </span>
          </div>
          <div className="border-t pt-1 mt-1">
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Sessions:</span>
              <span className="font-medium">
                {data.total_sessions.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Total Reads:</span>
              <span className="font-medium">
                {data.total_reads.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Get all locations sorted by ID for consistency
  const locations = React.useMemo(() => {
    return Object.keys(byLocationData).sort();
  }, [byLocationData]);

  const hasLocationData = locations.length > 0;

  // Prepare data for by-zone view
  const byZoneChartData = React.useMemo(() => {
    if (!hasLocationData || viewMode === "overall") return [];

    // Transform by-location data into chart format
    // Get all unique dates
    const allDates = new Set<string>();
    Object.values(byLocationData).forEach((locData) => {
      locData.forEach((point) => allDates.add(point.date));
    });

    // Create data points with all locations
    return Array.from(allDates)
      .sort()
      .map((date) => {
        const dataPoint: any = { date };
        locations.forEach((loc) => {
          const locPoint = byLocationData[loc]?.find((p) => p.date === date);
          dataPoint[`loc_${loc}`] = locPoint?.match_rate || 0;
        });
        return dataPoint;
      });
  }, [byLocationData, locations, hasLocationData, viewMode]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center space-x-2">
              {trend?.isPositive ? (
                <TrendingUp className="h-5 w-5 text-green-600" />
              ) : (
                <TrendingDown className="h-5 w-5 text-red-600" />
              )}
              <span>Performance Trends</span>
            </CardTitle>
            <div>
              <CardDescription>
                Daily match rates and operational metrics
              </CardDescription>
              {trend && viewMode === "overall" && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground">
                    Current avg: {trend.recentAvg}%
                  </span>
                  <Badge
                    variant={trend.isPositive ? "default" : "destructive"}
                    className="text-xs"
                  >
                    {trend.isPositive ? "+" : "-"}
                    {trend.value}%
                  </Badge>
                </div>
              )}
              {viewMode === "by-zone" && hasLocationData && (
                <div className="text-sm text-muted-foreground mt-1">
                  Comparing {locations.length} location
                  {locations.length > 1 ? "s" : ""}
                </div>
              )}
            </div>
          </div>

          {/* View Mode Toggle */}
          {hasLocationData && (
            <div className="flex gap-2">
              <Button
                variant={viewMode === "overall" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("overall")}
              >
                Overall
              </Button>
              <Button
                variant={viewMode === "by-zone" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("by-zone")}
              >
                By Zone
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Metric toggles (only show for overall view) */}
        {viewMode === "overall" && (
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(METRIC_LABELS).map(([key, label]) => (
              <Button
                key={key}
                variant={visibleMetrics[key] ? "default" : "outline"}
                size="sm"
                onClick={() => toggleMetric(key)}
                className="text-xs"
                style={{
                  borderColor: visibleMetrics[key]
                    ? METRIC_COLORS[key as keyof typeof METRIC_COLORS]
                    : undefined,
                  backgroundColor: visibleMetrics[key]
                    ? METRIC_COLORS[key as keyof typeof METRIC_COLORS]
                    : undefined,
                }}
              >
                <span
                  className="w-3 h-3 rounded-full mr-2"
                  style={{
                    backgroundColor:
                      METRIC_COLORS[key as keyof typeof METRIC_COLORS],
                  }}
                />
                {label}
              </Button>
            ))}
          </div>
        )}

        {/* Chart */}
        <div className="h-80">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-muted-foreground">Loading trends...</div>
            </div>
          ) : data.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-muted-foreground">
                No trend data available for selected period
              </div>
            </div>
          ) : viewMode === "by-zone" ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={byZoneChartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                  className="text-xs fill-muted-foreground"
                />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                  className="text-xs fill-muted-foreground"
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload || !payload.length) return null;
                    const data = payload[0].payload;
                    return (
                      <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                        <div className="font-semibold mb-2">
                          {new Date(data.date).toLocaleDateString()}
                        </div>
                        <div className="space-y-1 text-sm">
                          {locations.map((loc, idx) => (
                            <div
                              key={loc}
                              className="flex items-center justify-between gap-4"
                            >
                              <span className="flex items-center gap-2">
                                <span
                                  className="w-3 h-3 rounded-full"
                                  style={{
                                    backgroundColor:
                                      LOCATION_COLORS[
                                        idx % LOCATION_COLORS.length
                                      ],
                                  }}
                                />
                                <span className="text-muted-foreground">
                                  Zone {loc}:
                                </span>
                              </span>
                              <span className="font-medium">
                                {data[`loc_${loc}`]?.toFixed(2) || 0}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }}
                />

                <ReferenceLine
                  y={targetMatchRate}
                  stroke="#94a3b8"
                  strokeDasharray="5 5"
                  label={{
                    value: `Target ${targetMatchRate}%`,
                    position: "right",
                    fill: "#64748b",
                    fontSize: 12,
                  }}
                />

                {/* One line per location */}
                {locations.map((loc, idx) => (
                  <Line
                    key={loc}
                    type="monotone"
                    dataKey={`loc_${loc}`}
                    stroke={LOCATION_COLORS[idx % LOCATION_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    name={`Zone ${loc}`}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                  className="text-xs fill-muted-foreground"
                />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                  className="text-xs fill-muted-foreground"
                />
                <Tooltip content={<CustomTooltip />} />

                {/* Target baseline */}
                <ReferenceLine
                  y={targetMatchRate}
                  stroke="#94a3b8"
                  strokeDasharray="5 5"
                  label={{
                    value: `Target ${targetMatchRate}%`,
                    position: "right",
                    fill: "#64748b",
                    fontSize: 12,
                  }}
                />

                {/* Metric lines */}
                {visibleMetrics.match_rate && (
                  <Line
                    type="monotone"
                    dataKey="match_rate"
                    stroke={METRIC_COLORS.match_rate}
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Match Rate"
                  />
                )}
                {visibleMetrics.exact_rate && (
                  <Line
                    type="monotone"
                    dataKey="exact_rate"
                    stroke={METRIC_COLORS.exact_rate}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="Exact Match"
                  />
                )}
                {visibleMetrics.fuzzy_rate && (
                  <Line
                    type="monotone"
                    dataKey="fuzzy_rate"
                    stroke={METRIC_COLORS.fuzzy_rate}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="Fuzzy Match"
                  />
                )}
                {visibleMetrics.orphan_rate && (
                  <Line
                    type="monotone"
                    dataKey="orphan_rate"
                    stroke={METRIC_COLORS.orphan_rate}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    strokeDasharray="5 5"
                    name="Orphan Rate"
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
